import { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import useFitRows from '../../../hooks/useFitRows';
import {
  AreaChart, Area, Tooltip, ReferenceLine,
  ResponsiveContainer, XAxis, YAxis,
} from 'recharts';
import {
  ChevronDown, ChevronUp, Eye, EyeOff,
  ArrowRight, ArrowDown, ArrowUp, Check, Search,
} from 'lucide-react';
import useMediaQuery from '../../../hooks/useMediaQuery';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import MT5Modal from '../../../components/MT5Modal/MT5Modal';
import Pagination from '../../../components/Pagination/Pagination';
import PageFooter from '../../../components/PageFooter/PageFooter';
import api from '../../../services/api';
import styles from './Assets.module.css';

const RETURN_OPTIONS = ['All time return', 'This months return', "Today's return"];

const PERIODS = ['1D', '1W', '1M', '3M', 'YTD', '1Y', 'ALL'];

/* Rows per page in the trade tables. Fixed rather than measured (cf. useFitRows
   on the assets table above): these sit below the fold, so there is no window
   height left to fit them to. */
const TRADES_PER_PAGE = 6;

/* The same breakpoint the stylesheet uses for the mobile layout. The trade
   tables are seven columns wide and unreadable on a phone, so below this they
   are replaced by a pointer to the desktop view — and never fetched, since the
   history call is the expensive one. */
const DESKTOP_QUERY = '(min-width: 768px)';

/* Qualifies the change shown under the balance — must track the selected tab,
   since the figure beside it is the change across that same window. */
const PERIOD_LABELS = {
  '1D':  'past day',
  '1W':  'past week',
  '1M':  'past month',
  '3M':  'past 3 months',
  'YTD': 'year-to-date',
  '1Y':  'past year',
  'ALL': 'all-time',
};

/* ── Formatting helpers ── */
const money = (n) =>
  `$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
const signedMoney = (n) =>
  `${Number(n) >= 0 ? '+' : '-'}${money(n)}`;
const plainMoney = (n) =>
  `${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
const pct = (n) =>
  `${Number(n) >= 0 ? '+' : ''}${(Number(n) || 0).toFixed(2)}%`;
const dollars = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Sign ahead of the symbol — "-$62.52", not "$-62.52".
const signedDollars = (n) =>
  `${Number(n) < 0 ? '-' : '+'}${dollars(Math.abs(Number(n) || 0))}`;

/* ── Trade table formatting ──
   The money columns carry the minus ahead of the symbol ("-$1.32", not
   "$-1.32") and no plus on the way up, matching the design. */
const tradeMoney = (n) =>
  `${Number(n) < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* MTAPI stamps trades in broker server time and sends them without a zone, so
   they are pinned to UTC on the way in and read back in UTC — anything else
   would re-interpret them in the reader's zone and shift every time on screen.
   The result is the same clock the MT5 terminal shows. */
const tradeTime = (stamp) => {
  if (!stamp) return '-';
  const d = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(stamp) ? stamp : `${stamp}Z`);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })} ` +
         `${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })}`;
};

/* Prices are quoted at the symbol's own precision — 5 decimals on EURAUD, 3 on
   USDJPY, 2 on an index — so the row carries the instrument's `digits` and the
   price is shown to exactly that, never rounded to a flat two.

   No currency symbol: a price is quoted in the pair's counter currency, so the
   1.16723 on EURUSD is dollars per euro and the 0.71100 on AUDUSD is dollars per
   Australian dollar. Prefixing "$" would label them all as dollar amounts, which
   only the money columns beside them actually are. */
const tradePrice = (n, digits) => {
  const dp = Number.isFinite(Number(digits)) && Number(digits) > 0 ? Number(digits) : 2;
  return (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

// Lots arrive at broker precision (0.00119999); shown as sent rather than
// rounded, so the size is recognisable against the platform.
const tradeLots = (n) => (n == null ? '-' : String(n));

// The hovered point's return in dollars, and as a percentage of the equity it is
// measured against — the period's opening equity (see chartDataWithRef).
function pointReturn(point) {
  if (point?.base == null) return null;
  return `${signedDollars(point.value)} (${point.base ? pct((point.value / point.base) * 100) : '-'})`;
}

/* Picks the return figures for the currently selected dropdown option.
   Only "All time return" has a $ amount; month/today are percentage-only. */
function pickReturn(asset, filter) {
  const r = asset.returns || {};
  if (filter === 'This months return') return { amount: null, percentage: r.thisMonth?.percentage ?? null };
  if (filter === "Today's return")     return { amount: null, percentage: r.today?.percentage ?? null };
  return { amount: r.allTime?.amount ?? 0, percentage: r.allTime?.percentage ?? 0 };
}

/* Fallback label when the API omits one. Every range is a trailing UTC window of
   daily points, except 1D which is 24h at snapshot resolution. */
function fmtChartDate(iso, range) {
  const d = new Date(iso);
  if (range === '1D') {
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      hour12: false, timeZone: 'UTC',
    }) + ' UTC';
  }
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

/* ── Bridges recharts hover state to external setState ── */
function TooltipBridge({ active, payload, onUpdate }) {
  const point = active && payload?.length ? payload[0].payload : null;
  useLayoutEffect(() => { onUpdate(point); });
  return null;
}

function useIsDark() {
  const check = () => {
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'dark' || (attr !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  };
  const [dark, setDark] = useState(check);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(check()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/* ── Square dot for reference line intersection ── */
function SquareDot({ cx, cy, fill }) {
  const s = 9;
  return <rect x={cx - s / 2} y={cy - s / 2} width={s} height={s} fill={fill} stroke="#888888" strokeWidth={2} />;
}

/* ── "$0.00" pill riding the right end of the zero line ── */
function ZeroLabel({ viewBox, dark }) {
  const { x = 0, y = 0, width = 0 } = viewBox || {};
  const w = 54, h = 20;
  const left = x + width - w - 8;
  return (
    <g>
      <rect x={left} y={y - h / 2} width={w} height={h} rx={h / 2}
            fill={dark ? '#2A2F2F' : '#EAECEC'} />
      <text x={left + w / 2} y={y + 4} textAnchor="middle" fontSize={12}
            fill={dark ? '#B0B5B5' : '#525C5C'}>$0.00</text>
    </g>
  );
}

/* ── Main component ── */
function Assets() {
  usePageTitle('Assets');
  const isDark = useIsDark();
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [accountOpen, setAccountOpen]         = useState(false);
  const [searchMode, setSearchMode]           = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [balanceVisible, setBalanceVisible]   = useState(true);
  const [period, setPeriod]                   = useState('1D');
  const [returnFilter, setReturnFilter]       = useState('All time return');
  const [returnOpen, setReturnOpen]           = useState(false);
  const [mt5Login, setMt5Login]               = useState(null);
  const [activePoint, setActivePoint]         = useState(null);
  const [nameSort, setNameSort]               = useState(null); // null | 'asc' | 'desc'
  const [assetsPage, setAssetsPage]           = useState(1);

  /* ── Trades (single-account view only) ── */
  const [tradeTab, setTradeTab]     = useState('open'); // 'open' | 'history'
  const [tradePage, setTradePage]   = useState(1);
  const [trades, setTrades]         = useState({ items: [], totalPages: 1, total: 0 });
  const [tradesLoading, setTradesLoading] = useState(false);
  const [openCount, setOpenCount]   = useState(null);
  // Users on the server's blacklist get no "Open positions" tab. null until the
  // first overview answers, so no trades are asked for before it's known.
  const [hideOpenPositions, setHideOpenPositions] = useState(null);
  // Starting point only — the live count is measured from the window height
  // so the table ends where the screen does. See useFitRows.
  const ASSETS_PAGE_SIZE = 5;

  /* ── Server data ── */
  const [accounts, setAccounts]       = useState([]);   // [{ login, accountName }]
  const [overview, setOverview]       = useState(null); // { totalBalance, pastDay, totalRealizedProfit, assets }
  const [chartSeries, setChartSeries] = useState([]);   // [{ date, balance, equity }]
  const [chartDeposit, setChartDeposit] = useState(0);  // Σ depositCorrection over the scope
  const [periodChange, setPeriodChange] = useState(0);  // $ change over the selected range
  const [loading, setLoading]         = useState(true);

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  // The trade tables belong to one account, so "All accounts" has nothing to
  // show them for — that view is the portfolio, not a position book.
  const singleAccount = selectedAccount !== 'all';
  const showTrades = singleAccount && isDesktop;
  const tradesReady = showTrades && hideOpenPositions !== null;
  const activeTradeTab = hideOpenPositions ? 'history' : tradeTab;

  /* Switching accounts makes the current tab and page meaningless. Reset during
     render rather than in an effect, which would let one request go out against
     the previous account's page first. */
  const [lastTradeAccount, setLastTradeAccount] = useState(selectedAccount);
  if (lastTradeAccount !== selectedAccount) {
    setLastTradeAccount(selectedAccount);
    setTradeTab('open');
    setTradePage(1);
    setTrades({ items: [], totalPages: 1, total: 0 });
    setOpenCount(null);
  }

  const accountRef  = useRef(null);
  const returnRef   = useRef(null);
  const searchInput = useRef(null);

  const handleTooltipUpdate = useCallback((point) => {
    setActivePoint(prev => (prev === point ? prev : point));
  }, []);

  /* ── Fetch the dropdown accounts once ── */
  useEffect(() => {
    api.get('/assets/accounts')
      .then(({ data }) => setAccounts(Array.isArray(data) ? data : []))
      .catch(() => setAccounts([]));
  }, []);

  /* ── Fetch overview whenever the selected account changes ── */
  useEffect(() => {
    setLoading(true);
    setAssetsPage(1);
    const params = selectedAccount === 'all' ? {} : { login: selectedAccount };
    api.get('/assets/overview', { params })
      .then(({ data }) => {
        setOverview(data);
        setHideOpenPositions(!!data?.hideOpenPositions);
      })
      .catch(() => {
        setOverview(null);
        setHideOpenPositions(prev => prev ?? false);
      })
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  /* ── Fetch chart series whenever account or period changes ── */
  useEffect(() => {
    const params = { range: period };
    if (selectedAccount !== 'all') params.login = selectedAccount;
    api.get('/assets/chart', { params })
      .then(({ data }) => {
        // The API sends a range-appropriate `label` (e.g. "Jun 2026", "Q2 2026",
        // "2026"); fall back to client formatting only if it's absent.
        setChartSeries((data.series || []).map(p => ({
          date: p.label || fmtChartDate(p.time, data.range),
          balance: Number(p.balance),
          equity: Number(p.equity),
          deposit: Number(p.deposit),
          // Break-even for this range: where equity stood when the period opened.
          base: Number(p.base),
        })));
        setPeriodChange(Number(data.change) || 0);
        setChartDeposit(Number(data.deposit) || 0);
      })
      .catch(() => { setChartSeries([]); setPeriodChange(0); setChartDeposit(0); });
  }, [selectedAccount, period]);

  /* The count beside the "Open positions" tab, which has to stay right while the
     history tab is the one being paged. One row is enough — the envelope carries
     the total, and the server has the list cached either way. */
  useEffect(() => {
    if (!tradesReady || hideOpenPositions) return undefined;
    let active = true;
    api.get(`/assets/trades/${selectedAccount}`, { params: { state: 'open', limit: 1 } })
      .then(({ data }) => { if (active) setOpenCount(Number(data.total) || 0); })
      .catch(() => { if (active) setOpenCount(null); });
    return () => { active = false; };
  }, [selectedAccount, tradesReady, hideOpenPositions]);

  /* The active tab's page of trades. Paging is server-side: the client only ever
     holds one page, so ordering it here would apply to that page alone. */
  useEffect(() => {
    if (!tradesReady) return undefined;
    let active = true;
    setTradesLoading(true);
    api.get(`/assets/trades/${selectedAccount}`, {
      params: { state: activeTradeTab, page: tradePage, limit: TRADES_PER_PAGE },
    })
      .then(({ data }) => {
        if (!active) return;
        setTrades({
          items: Array.isArray(data.items) ? data.items : [],
          totalPages: Math.max(1, Number(data.totalPages) || 1),
          total: Number(data.total) || 0,
        });
      })
      .catch(() => { if (active) setTrades({ items: [], totalPages: 1, total: 0 }); })
      .finally(() => { if (active) setTradesLoading(false); });
    return () => { active = false; };
  }, [selectedAccount, tradesReady, activeTradeTab, tradePage]);

  const switchTradeTab = (next) => { setTradeTab(next); setTradePage(1); };

  const accountOptions = useMemo(() => [
    { id: 'all', label: 'All accounts' },
    ...accounts.map(a => ({ id: String(a.login), label: a.accountName })),
  ], [accounts]);

  const assetRows = useMemo(() => overview?.assets ?? [], [overview]);
  const isEmpty = !loading && accounts.length === 0;

  const sortedAssets = useMemo(() => {
    if (!nameSort) return assetRows;
    return [...assetRows].sort((a, b) =>
      nameSort === 'asc'
        ? a.accountName.localeCompare(b.accountName)
        : b.accountName.localeCompare(a.accountName)
    );
  }, [nameSort, assetRows]);

  const assetsPaginationRef = useRef(null);
  const footerRef           = useRef(null);
  const { bodyRef: assetsBodyRef, rowsPerPage: assetsRowsPerPage } = useFitRows({
    fallback: ASSETS_PAGE_SIZE,
    belowRefs: [assetsPaginationRef, footerRef],
    min: 6,
    max: 30,
    gutter: 24, // .content bottom padding
  });

  const assetsTotalPages = Math.max(1, Math.ceil(sortedAssets.length / assetsRowsPerPage));
  const assetsCurrentPage = Math.min(assetsPage, assetsTotalPages);
  const assetsPageRows = sortedAssets.slice((assetsCurrentPage - 1) * assetsRowsPerPage, assetsCurrentPage * assetsRowsPerPage);

  const restingBalance   = overview?.totalBalance ?? 0;
  // Starting balance = Σ depositCorrection across the scoped DFY, deployed
  // accounts. The chart endpoint sends the same figure for the scope it plotted;
  // prefer it so the line and its baseline always come from one response.
  const startBalance     = chartDeposit || overview?.startingBalance || restingBalance;
  const displayBalance   = activePoint?.balance ?? restingBalance;

  // The line plots the return, not the balance, so the $0.00 baseline is a real
  // line on the chart — break-even — and a point sits exactly its return above or
  // below it. The return is measured against where equity stood when the selected
  // period opened, which is what the period tabs mean:
  //
  //   return $ = equity now − equity when the period opened
  //   return % = (return $ / equity when the period opened) × 100
  //
  // So 1D reads against yesterday's close, 1W against the equity a week ago, and
  // ALL — having no previous period — against the deposit. Each point carries its
  // own baseline, counting only the accounts trading by then; the balance rides
  // along for the figure above the chart.
  const chartDataWithRef = useMemo(
    () => chartSeries.map(d => ({
      date: d.date,
      balance: d.balance,
      deposit: d.deposit,
      value: d.equity - d.base,
      base: d.base,
      refValue: 0,
    })),
    [chartSeries]
  );

  // $0.00 floats to wherever the data puts it: centred when returns straddle
  // breakeven, pushed to the top when they are mostly negative, to the bottom when
  // mostly positive. Forcing zero into the domain is what keeps the line visible
  // against it when every point sits on one side.
  const returnDomain = useMemo(() => {
    const values = chartDataWithRef.map(d => d.value);
    const lo = Math.min(0, ...values);
    const hi = Math.max(0, ...values);
    const pad = (hi - lo) * 0.12 || 1;
    return [lo - pad, hi + pad];
  }, [chartDataWithRef]);

  // close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) closeAccountDropdown();
      if (returnRef.current  && !returnRef.current.contains(e.target))  setReturnOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const accountLabel = accountOptions.find(a => a.id === selectedAccount)?.label ?? 'All accounts';

  const filteredAccounts = useMemo(() =>
    accountOptions.filter(a => a.label.toLowerCase().includes(searchQuery.toLowerCase())),
    [searchQuery, accountOptions]
  );

  const closeAccountDropdown = () => {
    setAccountOpen(false);
    setSearchMode(false);
    setSearchQuery('');
  };

  return (
    <DashboardLayout>
    <div className={styles.assetsPage}>

      {/* Account selector */}
      <div className={styles.accountSelector} ref={accountRef}>
        <button className={styles.accountTrigger} onClick={() => setAccountOpen(p => !p)}>
          {accountLabel.toUpperCase()}
          {accountOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>

        {accountOpen && (
          <div className={styles.accountDropdown}>
            {searchMode ? (
              <div className={styles.searchRow}>
                <Search size={15} className={styles.searchRowIcon} />
                <input
                  ref={searchInput}
                  autoFocus
                  className={styles.searchRowInput}
                  placeholder="Search accounts…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            ) : (
              <div className={styles.dropdownHeader}>
                <span className={styles.dropdownTitle}>Asset view</span>
                <button
                  className={styles.dropdownSearchBtn}
                  onClick={() => { setSearchMode(true); setTimeout(() => searchInput.current?.focus(), 0); }}
                >
                  <Search size={14} />
                </button>
              </div>
            )}

            {filteredAccounts.length === 0 ? (
              <div className={styles.noResults}>
                No results found<br />for "{searchQuery}"
              </div>
            ) : (
              filteredAccounts.map(acc => (
                <div
                  key={acc.id}
                  className={`${styles.accountOption} ${acc.id !== 'all' ? styles.accountOptionIndent : ''} ${selectedAccount === acc.id ? styles.accountOptionActive : ''}`}
                  onClick={() => { setSelectedAccount(acc.id); closeAccountDropdown(); }}
                >
                  {selectedAccount === acc.id
                    ? <Check size={13} className={styles.checkIcon} />
                    : <span style={{ width: 13 }} />}
                  {acc.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Balance */}
      <div className={styles.balanceRow}>
        <span className={styles.balanceValue}>
          {!balanceVisible ? '$·······' : dollars(displayBalance)}
        </span>
        <span className={styles.balanceCurrency}>USD</span>
        {/* Toggles on an empty account too — with no accounts the balance is a
            genuine $0.00, so there's nothing to special-case. */}
        <button
          className={styles.eyeBtn}
          onClick={() => setBalanceVisible(p => !p)}
          aria-label={balanceVisible ? 'Hide balance' : 'Show balance'}
        >
          {balanceVisible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {(() => {
        // Fixed for the selected range (server-computed) — hovering the chart
        // moves the balance above, not this.
        const isNegative = !isEmpty && periodChange < 0;
        return (
          <div className={`${styles.pastDay}${isNegative ? ` ${styles.pastDayNegative}` : ''}`}>
            <span>{!balanceVisible ? '$·····' : dollars(periodChange)}</span>
            <span>{PERIOD_LABELS[period]}</span>
            {isNegative ? <ArrowDown size={12} /> : <ArrowRight size={12} />}
          </div>
        );
      })()}

      {/* Chart */}
      {isEmpty
        ? <div className={styles.chartWrapper} style={{ height: 290 }} />
        : (
        <div className={styles.chartWrapper}>
          {activePoint && (
            <div className={styles.chartDate}>{activePoint.date}</div>
          )}
          {activePoint && (
            <div className={styles.chartLegend}>
              <div className={styles.legendItem}>
                <span className={styles.legendLabel}>
                  <span className={styles.legendIcon}>
                    <span className={styles.legendLine} />
                    <span className={styles.legendDot} />
                    <span className={styles.legendLine} />
                  </span>
                  Return
                </span>
                <span className={styles.legendValue}>{pointReturn(activePoint) ?? '-'}</span>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendLabel}>
                  <span className={styles.legendIcon}>
                    <span className={styles.legendLineDashed} />
                    <span className={styles.legendSquare} />
                    <span className={styles.legendLineDashed} />
                  </span>
                  {selectedAccount === 'all' ? 'Deposits' : 'Deposit'}
                </span>
                <span className={styles.legendValue}>{dollars(activePoint.deposit ?? startBalance)}</span>
              </div>
            </div>
          )}

          <ResponsiveContainer width="100%" height={290}>
            <AreaChart
              data={chartDataWithRef}
              margin={{ top: 72, right: 0, left: 0, bottom: 0 }}
            >
              <XAxis dataKey="date" hide axisLine={false} tickLine={false} />
              <YAxis hide domain={returnDomain} />
              <Tooltip
                content={<TooltipBridge onUpdate={handleTooltipUpdate} />}
                cursor={{ stroke: isDark ?  '#B0B5B5' : '#525C5C', strokeWidth: 1 }}
              />
              <ReferenceLine
                y={0}
                stroke={isDark ?  '#B0B5B5' : '#525C5C'}
                strokeDasharray="4 4"
                label={<ZeroLabel dark={isDark} />}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#83C35F"
                strokeWidth={2}
                fill="none"
                dot={false}
                activeDot={{ r: 5, fill: '#83C35F', stroke: '#111111', strokeWidth: 2 }}
              />
              <Area
                type="monotone"
                dataKey="refValue"
                stroke="none"
                fill="none"
                dot={false}
                activeDot={<SquareDot fill="transparent" />}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        )
      }

      {/* Period tabs + total profit */}
      <div className={styles.periodRow}>
        <div className={styles.periodTabs}>
          {PERIODS.map(p => (
            <button
              key={p}
              className={`${styles.periodTab} ${period === p ? styles.periodTabActive : ''}`}
              onClick={() => setPeriod(p)}
            >
              {p}
            </button>
          ))}
        </div>
        <span className={styles.totalProfit}>
          {selectedAccount === 'all' ? 'TOTAL REALIZED PROFIT' : 'REALIZED PROFIT'}:
          <span className={styles.totalProfitValue}>
            {isEmpty ? '-' : plainMoney(overview?.totalRealizedProfit)}
          </span>
        </span>
      </div>

      {/* My assets */}
      <h2 className={styles.sectionTitle}>My assets</h2>
      <div className={styles.tableSection}>
        <table className={`${styles.table} ${isEmpty || sortedAssets.length === 0 ? styles.tableEmpty : ''}`}>
          <thead>
            <tr>
              <th>
                <button
                  className={styles.thBtn}
                  onClick={() => setNameSort(s => s === 'asc' ? 'desc' : 'asc')}
                >
                  {nameSort === 'asc' ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                  Account name
                </button>
              </th>
              <th className={styles.mobileHide}>Balance</th>
              <th className={styles.mobileHide}>Equity</th>
              <th className={styles.returnTh} ref={returnRef}>
                <button className={styles.thBtn} onClick={() => setReturnOpen(p => !p)}>
                  {returnFilter} {returnOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {returnOpen && (
                  <div className={styles.returnDropdown}>
                    {RETURN_OPTIONS.map(opt => (
                      <div
                        key={opt}
                        className={`${styles.returnOption} ${returnFilter === opt ? styles.returnOptionActive : ''}`}
                        onClick={() => { setReturnFilter(opt); setReturnOpen(false); }}
                      >
                        <span className={styles.returnCheckSlot}>
                          {returnFilter === opt && <Check size={12} className={styles.checkIcon} />}
                        </span>
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </th>
            </tr>
          </thead>
          <tbody ref={assetsBodyRef}>
            {isEmpty || sortedAssets.length === 0
              ? <tr><td colSpan={4} className={styles.emptyCell}>No synced accounts yet</td></tr>
              : assetsPageRows.map(asset => {
                const ret = pickReturn(asset, returnFilter);
                const positive = (ret.amount ?? ret.percentage ?? 0) >= 0;
                return (
                <tr key={asset.login}>
                  <td>
                    <div className={styles.accountName}>
                      {asset.accountName}
                      {asset.badge && <span className={styles.groupBadge}>{asset.badge}</span>}
                    </div>
                    {/* Blacklisted users don't get the MT5 login modal at all */}
                    {hideOpenPositions === false && (
                      <button className={styles.mt5Link} onClick={() => setMt5Login(asset.login)}>
                        MT5 login <ArrowRight size={11} />
                      </button>
                    )}
                  </td>
                  <td className={styles.mobileHide}>{money(asset.balance)}</td>
                  <td className={styles.mobileHide}>{money(asset.equity)}</td>
                  <td>
                    {ret.amount != null && <div>{signedMoney(ret.amount)}</div>}
                    <div className={`${styles.returnPct} ${positive ? styles.returnPositive : styles.returnNegative}`}>
                      {ret.percentage == null ? '-' : pct(ret.percentage)}
                    </div>
                  </td>
                </tr>
                );
              })
            }
          </tbody>
        </table>

        <div ref={assetsPaginationRef}>
          {!isEmpty && sortedAssets.length > 0 && <Pagination page={assetsCurrentPage} totalPages={assetsTotalPages} onPageChange={setAssetsPage} variant="outline" />}
        </div>
      </div>

      {/* ── Trades for the selected account ── */}
      {singleAccount && (showTrades ? (
        <>
          <div className={styles.tradeTabs} role="tablist">
            {!hideOpenPositions && (
              <button
                role="tab"
                aria-selected={activeTradeTab === 'open'}
                className={`${styles.tradeTab} ${activeTradeTab === 'open' ? styles.tradeTabActive : ''}`}
                onClick={() => switchTradeTab('open')}
              >
                Open positions ({openCount == null ? '-' : openCount})
              </button>
            )}
            <button
              role="tab"
              aria-selected={activeTradeTab === 'history'}
              className={`${styles.tradeTab} ${activeTradeTab === 'history' ? styles.tradeTabActive : ''}`}
              onClick={() => switchTradeTab('history')}
            >
              Trade history
            </button>
          </div>

          <div className={styles.tradeSection}>
            <table className={styles.tradeTable}>
              <thead>
                <tr>
                  <th>Symbol</th>
                  {/* The two tabs report different ends of a trade's life. */}
                  <th>{activeTradeTab === 'open' ? 'Open time' : 'Close time'}</th>
                  <th className={styles.numCol}>Price</th>
                  <th className={styles.numCol}>Lots</th>
                  <th className={styles.numCol}>Commissions</th>
                  <th className={styles.numCol}>Swap</th>
                  <th className={styles.numCol}>Trade P/L</th>
                </tr>
              </thead>
              <tbody>
                {tradesLoading ? (
                  <tr><td colSpan={7} className={styles.tradeEmptyCell}>Loading trades…</td></tr>
                ) : trades.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className={styles.tradeEmptyCell}>
                      {activeTradeTab === 'open' ? 'No open positions' : 'No closed trades yet'}
                    </td>
                  </tr>
                ) : trades.items.map(t => (
                  <tr key={t.id} className={styles.tradeRow}>
                    <td>
                      <span className={`${styles.side} ${t.side === 'sell' ? styles.sideSell : styles.sideBuy}`}>
                        {t.side === 'sell' ? 'SELL' : 'BUY'}
                      </span>
                      <span className={styles.tradeSymbol}>{t.symbol}</span>
                    </td>
                    <td>{tradeTime(t.time)}</td>
                    <td className={styles.numCol}>{tradePrice(t.price, t.digits)}</td>
                    <td className={styles.numCol}>{tradeLots(t.lots)}</td>
                    <td className={styles.numCol}>{tradeMoney(t.commission)}</td>
                    <td className={styles.numCol}>{tradeMoney(t.swap)}</td>
                    <td className={`${styles.numCol} ${t.profit < 0 ? styles.tradeDown : ''}`}>
                      {tradeMoney(t.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={tradePage} totalPages={trades.totalPages} onPageChange={setTradePage} variant="outline" />
          </div>
        </>
      ) : (
        /* Seven columns of trade data need the width to be readable, so a phone
           gets the account summary above and a pointer to the desktop view. */
        <div className={styles.deskHint}>In-depth view on desktop</div>
      ))}

      {mt5Login != null && <MT5Modal login={mt5Login} onClose={() => setMt5Login(null)} />}
    </div>
    <div>
<PageFooter ref={footerRef} />
    </div>

    </DashboardLayout>
  );
}

export default Assets;
